import { Card } from "react-bootstrap";

// --- Types ---

export interface TournamentData {
  name: string;
  location: string;
  setCount: number;
  entrants: number;
  date: string;
  playlistId?: string;
  // URL slug for this tournament's dedicated focused page (/<slug>).
  slug: string;
}

// --- TournamentList ---

interface TournamentListProps {
  tournaments: TournamentData[];
  selectedTournament: string | null;
  onSelectTournament: (name: string) => void;
  onOpenPage: (name: string) => void;
}

export function TournamentList({
  tournaments,
  selectedTournament,
  onSelectTournament,
  onOpenPage,
}: TournamentListProps) {
  return (
    <div className="d-flex flex-column gap-2">
      {tournaments.map((t) => (
        <TournamentCard
          key={t.name}
          tournament={t}
          isSelected={t.name === selectedTournament}
          onSelect={() => onSelectTournament(t.name)}
          onOpenPage={() => onOpenPage(t.name)}
        />
      ))}
    </div>
  );
}

// --- TournamentCard ---

interface TournamentCardProps {
  tournament: TournamentData;
  isSelected: boolean;
  onSelect: () => void;
  onOpenPage: () => void;
}

function TournamentCard({ tournament, isSelected, onSelect, onOpenPage }: TournamentCardProps) {
  return (
    <Card
      role="button"
      tabIndex={0}
      className={`p-2 ${isSelected ? "is-selected" : ""}`}
      onClick={onSelect}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onSelect();
        }
      }}
    >
      <Card.Body className="p-0">
        <TournamentCardBody tournament={tournament} onOpenPage={onOpenPage} />
      </Card.Body>
    </Card>
  );
}

// --- TournamentCardBody ---
// The name/link row + location-date-setcount row shared by TournamentCard and
// any other place a tournament needs the same look (e.g. a player's
// cross-tournament set list, grouped by tournament).

export interface TournamentCardInfo {
  name: string;
  location: string;
  date: string;
  setCount: number;
  slug: string;
  playlistId?: string;
}

interface TournamentCardBodyProps {
  tournament: TournamentCardInfo;
  onOpenPage: () => void;
}

export function TournamentCardBody({ tournament, onOpenPage }: TournamentCardBodyProps) {
  const playlistUrl = tournament.playlistId
    ? `https://www.youtube.com/playlist?list=${tournament.playlistId}`
    : null;

  return (
    <>
      <div className="d-flex align-items-center mb-1 gap-2">
        <p className="mb-0 flex-grow-1 text-truncate">{tournament.name}</p>
        {playlistUrl && (
          <a
            href={playlistUrl}
            target="_blank"
            rel="noopener noreferrer"
            onClick={(e) => e.stopPropagation()}
            className="text-decoration-none flex-shrink-0"
            title="Watch playlist on YouTube"
          >
            <i className="bi bi-collection-play-fill" />
          </a>
        )}
        <a
          href={`/${tournament.slug}`}
          onClick={(e) => {
            // Plain left-click navigates within the SPA; modifier-clicks fall
            // through so "open in new tab" / copy-link still work.
            if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button !== 0) return;
            e.preventDefault();
            e.stopPropagation();
            onOpenPage();
          }}
          className="text-decoration-none flex-shrink-0"
          title="Open tournament page"
        >
          <i className="bi bi-box-arrow-up-right" />
        </a>
      </div>
      <div
        className="text-body-secondary gap-1 align-items-center"
        style={{
          display: "grid",
          gridTemplateColumns: "1fr auto 1fr",
        }}
      >
        <small>{tournament.location}</small>
        <small className="text-center">{tournament.date}</small>
        <small className="text-end">{tournament.setCount} sets</small>
      </div>
    </>
  );
}
